import { supabase } from '../lib/supabaseClient';
import type { UsageRecipe, UsageRecipeStatus } from '../types/recipes';

export type HelpfulUserSummary = {
  id: string;
  user_id: string;
  created_at: string;
};

/**
 * 公開済みの活用レシピを取得
 * @param params - 絞り込みパラメータ（subject, scene）
 * @returns 承認済みの活用レシピ一覧
 */
export async function fetchUsageRecipes(params?: {
  subject?: string;
  scene?: string;
}): Promise<UsageRecipe[]> {
  try {
    const { data, error } = await supabase
      .from('usage_recipes')
      .select('*')
      .eq('status', 'approved')
      .eq('is_published', true)
      .order('created_at', { ascending: false });

    if (error) throw error;

    let recipes = data || [];

    // フロント側で絞り込み
    if (params?.subject) {
      recipes = recipes.filter((r) => r.subject === params.subject);
    }
    if (params?.scene) {
      recipes = recipes.filter((r) => r.scene === params.scene);
    }

    return recipes;
  } catch (error) {
    console.error('活用レシピの取得に失敗しました:', error);
    throw error;
  }
}

/**
 * IDで活用レシピを1件取得
 * @param id - レシピID
 * @returns 活用レシピ（見つからない場合はnull）
 */
export async function fetchUsageRecipeById(
  id: string
): Promise<UsageRecipe | null> {
  try {
    const { data, error } = await supabase
      .from('usage_recipes')
      .select('*')
      .eq('id', id)
      .eq('status', 'approved')
      .eq('is_published', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null;
      }
      throw error;
    }

    return data;
  } catch (error) {
    console.error('活用レシピの取得に失敗しました:', error);
    throw error;
  }
}

/**
 * 単元ガイドに紐づく活用レシピを取得
 * @param materialId - 単元ガイドID
 * @param limit - 取得件数（デフォルト5件）
 * @returns 承認済みの活用レシピ一覧
 */
export async function fetchUsageRecipesByMaterial(
  materialId: string,
  limit = 5
): Promise<UsageRecipe[]> {
  try {
    const { data, error } = await supabase
      .from('usage_recipes')
      .select('*')
      .eq('status', 'approved')
      .eq('is_published', true)
      .eq('material_id', materialId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('単元ガイドの活用レシピ取得に失敗しました:', error);
    throw error;
  }
}

/**
 * 教材サイトに紐づく活用レシピを取得
 * @param siteId - 教材サイトID
 * @param limit - 取得件数（デフォルト5件）
 * @returns 承認済みの活用レシピ一覧
 */
export async function fetchUsageRecipesBySite(
  siteId: string,
  limit = 5
): Promise<UsageRecipe[]> {
  try {
    const { data, error } = await supabase
      .from('usage_recipes')
      .select('*')
      .eq('status', 'approved')
      .eq('is_published', true)
      .eq('site_id', siteId)
      .order('created_at', { ascending: false })
      .limit(limit);

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('教材サイトの活用レシピ取得に失敗しました:', error);
    throw error;
  }
}

/**
 * 新規活用レシピを投稿
 * @param payload - レシピデータ
 */
export async function createUsageRecipe(payload: {
  title: string;
  steps: string;
  summary?: string;
  subject?: string;
  grade_min?: number | null;
  grade_max?: number | null;
  scene?: string;
  group_type?: string;
  time_minutes?: number | null;
  material_id?: string | null;
  site_id?: string | null;
  tips?: string;
  cautions?: string;
  role?: string;
  author_name?: string;
}): Promise<void> {
  try {
    const { error } = await supabase.from('usage_recipes').insert({
      title: payload.title,
      steps: payload.steps,
      summary: payload.summary || null,
      subject: payload.subject || null,
      grade_min: payload.grade_min ?? null,
      grade_max: payload.grade_max ?? null,
      scene: payload.scene || null,
      group_type: payload.group_type || null,
      time_minutes: payload.time_minutes ?? null,
      material_id: payload.material_id || null,
      site_id: payload.site_id || null,
      tips: payload.tips || null,
      cautions: payload.cautions || null,
      role: payload.role || null,
      author_name: payload.author_name || null,
      // status は DB デフォルト 'pending' を使用
    });

    if (error) throw error;
  } catch (error) {
    console.error('活用レシピの投稿に失敗しました:', error);
    throw error;
  }
}

/**
 * 承認待ちの活用レシピを取得（管理用）
 * @param limit - 取得件数（デフォルト50件）
 * @returns 承認待ちの活用レシピ一覧
 */
export async function fetchPendingUsageRecipes(
  limit = 50
): Promise<UsageRecipe[]> {
  try {
    const { data, error } = await supabase
      .from('usage_recipes')
      .select('*')
      .eq('status', 'pending')
      .order('created_at', { ascending: true })
      .limit(limit);

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('承認待ち活用レシピの取得に失敗しました:', error);
    throw error;
  }
}

/**
 * 管理者用：全ての活用レシピを取得（ステータス問わず）
 * @returns 全ての活用レシピ一覧
 */
export async function fetchAllUsageRecipesForAdmin(): Promise<UsageRecipe[]> {
  try {
    const { data, error } = await supabase
      .from('usage_recipes')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('活用レシピの取得に失敗しました:', error);
    throw error;
  }
}

/**
 * 管理者用：全ての活用レシピを取得（fetchAllRecipes のエイリアス）
 * @returns 全ての活用レシピ一覧
 */
export async function fetchAllRecipes(): Promise<UsageRecipe[]> {
  return fetchAllUsageRecipesForAdmin();
}

/**
 * 活用レシピの承認・編集（管理用）
 * @param id - レシピID
 * @param params - 更新パラメータ
 * @returns 更新後の活用レシピ
 */
export async function updateUsageRecipeModeration(
  id: string,
  params: {
    status?: UsageRecipeStatus;
    title?: string;
    summary?: string | null;
    steps?: string | null;
    tips?: string | null;
    cautions?: string | null;
  }
): Promise<UsageRecipe> {
  try {
    const updateData: Record<string, unknown> = {};

    if (params.status !== undefined) {
      updateData.status = params.status;
    }
    if (params.title !== undefined) {
      updateData.title = params.title;
    }
    if (params.summary !== undefined) {
      updateData.summary = params.summary;
    }
    if (params.steps !== undefined) {
      updateData.steps = params.steps;
    }
    if (params.tips !== undefined) {
      updateData.tips = params.tips;
    }
    if (params.cautions !== undefined) {
      updateData.cautions = params.cautions;
    }

    const { data, error } = await supabase
      .from('usage_recipes')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    return data;
  } catch (error) {
    console.error('活用レシピの更新に失敗しました:', error);
    throw error;
  }
}

/**
 * 活用レシピの「役に立った」カウントを増やす
 * @param id - レシピID
 */
export async function incrementUsageRecipeHelpful(recipeId: string): Promise<void> {
  const { error } = await supabase.rpc('increment_usage_recipe_helpful', {
    p_recipe_id: recipeId,
  });
  if (error) {
    throw error;
  }
}

/**
 * ログインユーザー用：「役に立った」をインクリメント（1ユーザー1回制御）
 */
export async function incrementUsageRecipeHelpfulWithUser(
  recipeId: string
): Promise<UsageRecipe | null> {
  const { data, error } = await supabase.rpc('increment_usage_recipe_helpful_user', {
    p_recipe_id: recipeId,
  });

  if (error) {
    throw error;
  }

  return (data ?? null) as UsageRecipe | null;
}

/**
 * 管理者用：活用レシピのステータス更新
 * @param recipeId - レシピID
 * @param nextStatus - 変更後のステータス
 * @returns 更新後の活用レシピ
 */
export async function adminUpdateUsageRecipeStatus(
  recipeId: string,
  nextStatus: UsageRecipeStatus
): Promise<UsageRecipe> {
  const { data, error } = await supabase.rpc('admin_update_usage_recipe_status', {
    p_recipe_id: recipeId,
    p_status: nextStatus,
  });

  if (error) {
    throw error;
  }

  return data as UsageRecipe;
}

/**
 * 管理者用：活用レシピのステータス更新（updateRecipeStatus のエイリアス）
 * @param recipeId - レシピID
 * @param status - 変更後のステータス
 */
export async function updateRecipeStatus(
  recipeId: string,
  status: string
): Promise<void> {
  try {
    const { error } = await supabase
      .from('usage_recipes')
      .update({ status })
      .eq('id', recipeId);

    if (error) throw error;
  } catch (error) {
    console.error('レシピステータスの更新に失敗しました:', error);
    throw error;
  }
}

/**
 * 管理者用：活用レシピの内容を更新
 * @param id - レシピID
 * @param payload - 更新内容
 * @returns 更新後の活用レシピ
 */
export async function adminUpdateUsageRecipeContent(
  id: string,
  payload: {
    title: string;
    summary: string | null;
    grade_min: number | null;
    grade_max: number | null;
    scene: string | null;
  }
): Promise<UsageRecipe> {
  const { data, error } = await supabase.rpc('admin_update_usage_recipe_content', {
    p_recipe_id: id,
    p_title: payload.title,
    p_summary: payload.summary,
    p_grade_min: payload.grade_min,
    p_grade_max: payload.grade_max,
    p_scene: payload.scene,
  });

  if (error) {
    throw error;
  }

  return data as UsageRecipe;
}

/**
 * 管理者用：活用レシピを削除する
 * @param id - レシピID
 */
export async function adminDeleteUsageRecipe(id: string): Promise<void> {
  const { error } = await supabase.rpc('admin_delete_usage_recipe', {
    p_recipe_id: id,
  });

  if (error) {
    throw error;
  }
}

/**
 * 管理者用：活用レシピを削除（deleteRecipe のエイリアス）
 * @param recipeId - レシピID
 */
export async function deleteRecipe(recipeId: string): Promise<void> {
  return adminDeleteUsageRecipe(recipeId);
}

/**
 * 管理者用：活用レシピの「役に立った」押下ユーザー一覧を取得
 * @param recipeId - レシピID
 * @returns 押下ユーザーの一覧（新しい順）
 */
export async function adminFetchUsageRecipeHelpfulUsers(
  recipeId: string
): Promise<HelpfulUserSummary[]> {
  const { data, error } = await supabase.rpc(
    'admin_list_usage_recipe_helpful_users',
    { p_recipe_id: recipeId }
  );

  if (error) {
    console.error('役に立ったユーザー一覧の取得に失敗しました:', error);
    throw error;
  }

  return (data ?? []) as HelpfulUserSummary[];
}

/**
 * 活用レシピの公開設定を更新（管理用）
 * @param recipeId - レシピID
 * @param settings - 更新する公開設定
 */
export async function updateUsageRecipePublishSettings(
  recipeId: string,
  settings: {
    is_published: boolean;
    is_featured?: boolean;
    subject?: string;
    grade_min?: number | null;
    grade_max?: number | null;
  }
): Promise<void> {
  try {
    const updateData: Record<string, unknown> = {
      is_published: settings.is_published,
    };

    if (settings.is_featured !== undefined) {
      updateData.is_featured = settings.is_featured;
    }
    if (settings.subject !== undefined) {
      updateData.subject = settings.subject;
    }
    if (settings.grade_min !== undefined) {
      updateData.grade_min = settings.grade_min;
    }
    if (settings.grade_max !== undefined) {
      updateData.grade_max = settings.grade_max;
    }

    const { error } = await supabase
      .from('usage_recipes')
      .update(updateData)
      .eq('id', recipeId);

    if (error) throw error;
  } catch (error) {
    console.error('活用レシピの公開設定更新に失敗しました:', error);
    throw error;
  }
}

/**
 * ログインユーザー用：自分が「役に立った」を押したレシピ一覧を取得
 * @returns レシピ情報と押した日時のペア（新しい順）
 */
export async function fetchMyHelpfulRecipes(): Promise<{
  recipe: UsageRecipe;
  helpful_created_at: string;
}[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('ログインが必要です');
    }

    // helpful_users と usage_recipes を JOIN
    const { data, error } = await supabase
      .from('helpful_users')
      .select(`
        created_at,
        usage_recipes (*)
      `)
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    // データ整形
    const results = (data || [])
      .filter((item) => item.usage_recipes !== null)
      .map((item) => ({
        recipe: item.usage_recipes as unknown as UsageRecipe,
        helpful_created_at: item.created_at,
      }));

    return results;
  } catch (error) {
    console.error('自分の「役に立った」レシピの取得に失敗しました:', error);
    throw error;
  }
}

/**
 * ログインユーザー用：自分が投稿した活用レシピ一覧を取得（新着順）
 * @returns 自分の投稿レシピ一覧
 */
export async function fetchMyUsageRecipes(): Promise<UsageRecipe[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      throw new Error('ログインが必要です');
    }

    const { data, error } = await supabase
      .from('usage_recipes')
      .select('*')
      .eq('user_id', user.id)
      .order('created_at', { ascending: false });

    if (error) throw error;

    return data || [];
  } catch (error) {
    console.error('自分の投稿レシピの取得に失敗しました:', error);
    throw error;
  }
}
